import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Clock, XCircle } from 'lucide-react';
import barrelReplenishmentService from '../../../services/barrelReplenishmentService';

const ActiveTriggersPanel = ({ refreshKey }) => {
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadTriggers();
  }, [refreshKey, filter]);

  const loadTriggers = async () => {
    try {
      setLoading(true);
      const filters = filter !== 'all' ? { status: filter } : {};
      const data = await barrelReplenishmentService?.getReplenishmentTriggers(filters);
      setTriggers(data);
    } catch (error) {
      console.error('Error loading triggers:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending': case'processing':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-700',
      processing: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    return badges?.[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      processing: 'En cours',
      completed: 'Complété',
      failed: 'Échoué',
      cancelled: 'Annulé'
    };
    return labels?.[status] || status;
  };

  const getTriggerTypeLabel = (type) => {
    const labels = {
      low_stock: 'Stock Bas',
      critical_stock: 'Stock Critique',
      manual: 'Manuel',
      scheduled: 'Programmé'
    };
    return labels?.[type] || type;
  };

  const getBarrelTypeLabel = (type) => {
    return type === 'noir_270l' ? 'Noir 270L' : 'Bleu 220L';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          Déclencheurs de Réapprovisionnement
        </h2>
        <select
          value={filter}
          onChange={(e) => setFilter(e?.target?.value)}
          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">Tous</option>
          <option value="pending">En attente</option>
          <option value="processing">En cours</option>
          <option value="completed">Complétés</option>
          <option value="failed">Échoués</option>
        </select>
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {triggers?.map((trigger) => (
          <div
            key={trigger?.id}
            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                {getStatusIcon(trigger?.status)}
                <div>
                  <div className="font-semibold text-gray-900">
                    {getBarrelTypeLabel(trigger?.barrel_type)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {getTriggerTypeLabel(trigger?.trigger_type)}
                  </div>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(trigger?.status)}`}>
                {getStatusLabel(trigger?.status)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm mb-2">
              <div>
                <span className="text-gray-600">Stock au déclenchement:</span>
                <span className="ml-1 font-medium text-gray-900">
                  {trigger?.stock_level_at_trigger} unités
                </span>
              </div>
              <div>
                <span className="text-gray-600">Seuil:</span>
                <span className="ml-1 font-medium text-gray-900">
                  {trigger?.threshold_level} unités
                </span>
              </div>
            </div>

            {trigger?.purchase_order_id && (
              <div className="text-sm">
                <span className="text-gray-600">Bon de commande:</span>
                <span className="ml-1 font-medium text-blue-600">
                  {trigger?.barrel_purchase_orders?.po_number || 'N/A'}
                </span>
              </div>
            )}

            {trigger?.error_message && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                {trigger?.error_message}
              </div>
            )}

            <div className="mt-2 text-xs text-gray-500">
              Déclenché le {new Date(trigger?.triggered_at)?.toLocaleString('fr-FR')}
            </div>
          </div>
        ))}
      </div>
      {triggers?.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Clock className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>Aucun déclencheur actif</p>
        </div>
      )}
    </div>
  );
};

export default ActiveTriggersPanel;